import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Stack, TextField, Button, Chip, Paper,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem,
  FormControl, InputLabel, Snackbar, Avatar,
  Autocomplete, CircularProgress, alpha,
} from '@mui/material';
import AddIcon          from '@mui/icons-material/Add';
import EditIcon         from '@mui/icons-material/Edit';
import DeleteIcon       from '@mui/icons-material/DeleteOutline';
import GroupsIcon       from '@mui/icons-material/Groups';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonAddIcon    from '@mui/icons-material/PersonAdd';
import AccessTimeIcon   from '@mui/icons-material/AccessTime';
import ExpandMoreIcon   from '@mui/icons-material/ExpandMore';
import ExpandLessIcon   from '@mui/icons-material/ExpandLess';
import { getAuthToken } from '../../services/authSession';

const API_URL = process.env.REACT_APP_API_URL || '';
const DAYS    = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const D_SHORT = { Monday:'H', Tuesday:'M', Wednesday:'M', Thursday:'E', Friday:'P', Saturday:'S', Sunday:'D' };
const DAY_ALB = { Monday:'E Hënë', Tuesday:'E Martë', Wednesday:'E Mërkurë', Thursday:'E Enjte', Friday:'E Premte', Saturday:'E Shtunë', Sunday:'E Diel' };
const PALETTE = ['#4F46E5','#0891B2','#059669','#D97706','#DC2626','#7C3AED','#BE185D','#0369A1'];

async function shiftFetch(path, opts = {}) {
  try {
    let token = '';
    token = getAuthToken();
    const res = await fetch(`${API_URL}${path}`, {
      ...opts,
      headers: { 'Content-Type':'application/json', ...(token ? { Authorization:`Bearer ${token}` } : {}), ...(opts.headers||{}) },
    });
    return res.json();
  } catch(e) { return { success: false, error: e.message }; }
}

const fmtDate = d => d ? new Date(d+'T12:00:00').toLocaleDateString('sq-AL',{day:'2-digit',month:'short'}) : '';
const initials = (n='') => n.split(' ').map(x=>x[0]||'').join('').toUpperCase().slice(0,2)||'?';

export default function ShiftPlanning() {
  const [groups,       setGroups]       = useState([]);
  const [plans,        setPlans]        = useState([]);
  const [workers,      setWorkers]      = useState([]);
  const [planWorkers,  setPlanWorkers]  = useState({});
  const [groupWorkers, setGroupWorkers] = useState({});
  const [expanded,     setExpanded]     = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [snack,        setSnack]        = useState('');

  const [groupDlg,    setGroupDlg]    = useState(null);
  const [planDlg,     setPlanDlg]     = useState(null);
  const [groupStaff,  setGroupStaff]  = useState(null);
  const [addWorkerTo, setAddWorkerTo] = useState(null);
  const [selWorker,   setSelWorker]   = useState(null);
  const [selGWorker,  setSelGWorker]  = useState(null);
  const [delDlg,      setDelDlg]      = useState(null);

  const loadGroups  = useCallback(() => shiftFetch('/api/shift-groups').then(r => { if(r.success) setGroups(r.data||[]); }), []);
  const loadPlans   = useCallback(() => shiftFetch('/api/shift-plans').then(r  => { if(r.success) setPlans(r.data||[]);  }), []);
  const loadWorkers = useCallback(() => shiftFetch('/api/query/selectworker').then(r => { if(r.success && r.data) setWorkers(r.data); }), []);

  const loadPW = useCallback(async id => {
    const r = await shiftFetch(`/api/shift-plans/${id}/workers`);
    if(r.success) setPlanWorkers(p => ({...p,[id]:r.data}));
  },[]);

  const loadGW = useCallback(async id => {
    const r = await shiftFetch(`/api/shift-groups/${id}/workers`);
    if(r.success) setGroupWorkers(p => ({...p,[id]:r.data}));
  },[]);

  useEffect(() => { loadGroups(); loadPlans(); loadWorkers(); }, [loadGroups,loadPlans,loadWorkers]);

  const toggleExpand = async id => {
    if(expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if(!planWorkers[id]) await loadPW(id);
  };

  /* ── Group CRUD ── */
  const saveGroup = async () => {
    if(!groupDlg?.name?.trim()) return;
    setLoading(true);
    const isNew = !groupDlg.id;
    await shiftFetch(isNew ? '/api/shift-groups' : `/api/shift-groups/${groupDlg.id}`, { method: isNew?'POST':'PUT', body: JSON.stringify(groupDlg) });
    setLoading(false); setGroupDlg(null); loadGroups();
    setSnack(isNew ? 'Grupi u krijua!' : 'Grupi u ruajt!');
  };

  const deleteGroup = async () => {
    await shiftFetch(`/api/shift-groups/${delDlg.id}`, { method:'DELETE' });
    setDelDlg(null); loadGroups(); setSnack('Grupi u fshi.');
  };

  /* ── Plan CRUD ── */
  const savePlan = async () => {
    if(!planDlg?.name?.trim()) return;
    setLoading(true);
    const isNew = !planDlg.id;
    await shiftFetch(isNew ? '/api/shift-plans' : `/api/shift-plans/${planDlg.id}`, {
      method: isNew?'POST':'PUT',
      body: JSON.stringify({ name:planDlg.name, groupId:planDlg.groupId||null, weekStart:planDlg.weekStart||null, weekEnd:planDlg.weekEnd||null, notes:planDlg.notes||'' }),
    });
    setLoading(false); setPlanDlg(null); loadPlans();
    setSnack(isNew ? 'Plani u krijua!' : 'Plani u ruajt!');
  };

  const deletePlan = async () => {
    await shiftFetch(`/api/shift-plans/${delDlg.planId}`, { method:'DELETE' });
    setDelDlg(null); loadPlans(); setSnack('Plani u fshi.');
  };

  /* ── Workers in plans ── */
  const addWorker = async () => {
    if(!selWorker || !addWorkerTo) return;
    setLoading(true);
    const r = await shiftFetch(`/api/shift-plans/${addWorkerTo}/workers`, {
      method:'POST', body: JSON.stringify({ workerId: selWorker.Id||selWorker.id, sendEmail:true }),
    });
    setLoading(false);
    if(r.success) { await loadPW(addWorkerTo); loadPlans(); setSelWorker(null); setSnack('Punëtori u shtua!'); }
    else setSnack(r.error||'Gabim');
  };

  const removeWorker = async () => {
    await shiftFetch(`/api/shift-plans/${delDlg.planId}/workers/${delDlg.workerId}`,{method:'DELETE'});
    const pid = delDlg.planId; setDelDlg(null);
    await loadPW(pid); loadPlans(); setSnack('Punëtori u hoq.');
  };

  /* ── Workers in groups ── */
  const addGWorker = async () => {
    if(!selGWorker || !groupStaff) return;
    setLoading(true);
    const r = await shiftFetch(`/api/shift-groups/${groupStaff.id}/workers`,{method:'POST',body:JSON.stringify({workerId:selGWorker.Id||selGWorker.id})});
    setLoading(false);
    if(r.success) { await loadGW(groupStaff.id); loadGroups(); setSelGWorker(null); setSnack('Punëtori u shtua!'); }
    else setSnack(r.error||'Gabim');
  };

  const removeGWorker = async () => {
    await shiftFetch(`/api/shift-groups/${delDlg.groupId}/workers/${delDlg.workerId}`,{method:'DELETE'});
    const gid=delDlg.groupId; setDelDlg(null);
    await loadGW(gid); loadGroups(); setSnack('Punëtori u hoq.');
  };

  const openGroupStaff = async group => {
    setGroupStaff(group); setSelGWorker(null);
    if(!groupWorkers[group.id]) await loadGW(group.id);
  };

  /* ═══════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════*/
  return (
    <Box>
      <Grid container spacing={2} alignItems="flex-start">

        {/* ══ LEFT: Grupet ════════════════════════════════════ */}
        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ border:'1px solid',borderColor:'divider',borderRadius:2.5,overflow:'hidden' }}>
            <Box sx={{ px:2,py:1.25,borderBottom:'1px solid',borderColor:'divider',
              background:'linear-gradient(135deg,#f0f4ff 0%,#e8effe 100%)',
              display:'flex',alignItems:'center',justifyContent:'space-between' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <GroupsIcon sx={{ fontSize:17,color:'primary.main' }} />
                <Typography variant="subtitle2" fontWeight={800} sx={{ fontSize:'0.82rem' }}>Grupet / Turnet</Typography>
                <Chip size="small" label={groups.length} sx={{ height:18,fontSize:10,bgcolor:'primary.main',color:'#fff' }} />
              </Stack>
              <Button size="small" variant="contained" startIcon={<AddIcon sx={{ fontSize:14 }} />}
                onClick={() => setGroupDlg({ name:'',description:'',color:'#4F46E5',startTime:'08:00',endTime:'16:00',daysOfWeek:['Monday','Tuesday','Wednesday','Thursday','Friday'] })}
                sx={{ textTransform:'none',fontSize:'0.75rem',borderRadius:1.5,height:28,px:1.5 }}>
                Shto grup
              </Button>
            </Box>

            <Box>
              {groups.length === 0 ? (
                <Box sx={{ p:3,textAlign:'center' }}>
                  <GroupsIcon sx={{ fontSize:32,color:'text.disabled',mb:0.5 }} />
                  <Typography variant="body2" color="text.secondary">Nuk ka grupe ende.</Typography>
                  <Typography variant="caption" color="text.disabled">Krijo grupin e parë për të organizuar oraret.</Typography>
                </Box>
              ) : groups.map((g, idx) => (
                <Box key={g.id} sx={{
                  display:'flex',alignItems:'flex-start',px:2,py:1.25,gap:1.5,
                  borderBottom: idx < groups.length-1 ? '1px solid' : 'none', borderColor:'divider',
                  '&:hover':{ bgcolor:'#F8FAFC' }, transition:'background .1s',
                }}>
                  <Box sx={{ width:4,minHeight:52,borderRadius:2,bgcolor:g.color,flexShrink:0,mt:0.25 }} />
                  <Box sx={{ flex:1,minWidth:0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb:0.4 }}>
                      <Typography variant="body2" fontWeight={700} sx={{ lineHeight:1.2 }}>{g.name}</Typography>
                      {g.description && (
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize:'0.68rem' }}>— {g.description}</Typography>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="center">
                      <Stack direction="row" alignItems="center" spacing={0.3}
                        sx={{ px:0.75,py:0.2,borderRadius:1,bgcolor:'#F1F5F9',border:'1px solid #E2E8F0' }}>
                        <AccessTimeIcon sx={{ fontSize:11,color:'text.secondary' }} />
                        <Typography sx={{ fontSize:10,fontWeight:700,color:'#334155' }}>{g.startTime||'—'} – {g.endTime||'—'}</Typography>
                      </Stack>
                      {(g.daysOfWeek||[]).map(d => (
                        <Box key={d} sx={{ width:18,height:18,borderRadius:'50%',
                          bgcolor: ['Saturday','Sunday'].includes(d) ? '#FEE2E2' : alpha(g.color,0.15),
                          border:`1px solid ${['Saturday','Sunday'].includes(d) ? '#FECACA' : alpha(g.color,0.3)}`,
                          display:'flex',alignItems:'center',justifyContent:'center' }}>
                          <Typography sx={{ fontSize:8,fontWeight:800,color:['Saturday','Sunday'].includes(d)?'#DC2626':g.color }}>{D_SHORT[d]}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={0.25} alignItems="center" flexShrink={0}>
                    <Tooltip title="Menaxho stafin">
                      <Chip icon={<GroupsIcon sx={{ fontSize:'11px !important' }} />}
                        label={(groupWorkers[g.id]?.length ?? g.workerCount ?? 0) + ' staf'}
                        size="small" variant="outlined" clickable color="primary"
                        onClick={() => openGroupStaff(g)}
                        sx={{ fontSize:10,height:22 }} />
                    </Tooltip>
                    <IconButton size="small" onClick={() => setGroupDlg({...g})} sx={{ p:0.4 }}>
                      <EditIcon sx={{ fontSize:14 }} />
                    </IconButton>
                    <IconButton size="small" color="error" sx={{ p:0.4 }}
                      onClick={() => setDelDlg({ type:'group',id:g.id,name:g.name })}>
                      <DeleteIcon sx={{ fontSize:14 }} />
                    </IconButton>
                  </Stack>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* ══ RIGHT: Planet ════════════════════════════════════ */}
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ border:'1px solid',borderColor:'divider',borderRadius:2.5,overflow:'hidden' }}>
            <Box sx={{ px:2,py:1.25,borderBottom:'1px solid',borderColor:'divider',
              background:'linear-gradient(135deg,#f0fdf8 0%,#e6faf3 100%)',
              display:'flex',alignItems:'center',justifyContent:'space-between' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CalendarMonthIcon sx={{ fontSize:17,color:'secondary.main' }} />
                <Typography variant="subtitle2" fontWeight={800} sx={{ fontSize:'0.82rem' }}>Planet e Planifikimit</Typography>
                <Chip size="small" label={plans.length} sx={{ height:18,fontSize:10,bgcolor:'secondary.main',color:'#fff' }} />
              </Stack>
              <Button size="small" variant="contained" color="secondary"
                startIcon={<AddIcon sx={{ fontSize:14 }} />}
                onClick={() => setPlanDlg({ name:'',groupId:'',weekStart:'',weekEnd:'',notes:'' })}
                sx={{ textTransform:'none',fontSize:'0.75rem',borderRadius:1.5,height:28,px:1.5 }}>
                Shto plan
              </Button>
            </Box>

            <Box>
              {plans.length === 0 ? (
                <Box sx={{ p:3,textAlign:'center' }}>
                  <CalendarMonthIcon sx={{ fontSize:32,color:'text.disabled',mb:0.5 }} />
                  <Typography variant="body2" color="text.secondary">Nuk ka plane ende.</Typography>
                  <Typography variant="caption" color="text.disabled">Krijo planin e parë të orarit.</Typography>
                </Box>
              ) : plans.map((plan, idx) => {
                const isOpen = expanded === plan.id;
                const pws    = planWorkers[plan.id] || [];
                const grp    = groups.find(g => g.id === plan.groupId);
                return (
                  <Box key={plan.id} sx={{ borderBottom: idx < plans.length-1 ? '1px solid' : 'none', borderColor:'divider' }}>
                    {/* Plan row */}
                    <Box sx={{ px:2,py:1.25,display:'flex',alignItems:'center',gap:1.5,
                      bgcolor: isOpen ? '#F0FDF4' : '#fff',
                      '&:hover':{ bgcolor: isOpen ? '#F0FDF4' : '#F8FAFC' }, transition:'background .1s' }}>
                      {grp
                        ? <Box sx={{ width:10,height:10,borderRadius:'50%',bgcolor:grp.color,flexShrink:0 }} />
                        : <Box sx={{ width:10,height:10,borderRadius:'50%',bgcolor:'#CBD5E1',flexShrink:0 }} />
                      }
                      <Box sx={{ flex:1,minWidth:0 }}>
                        <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                          <Typography variant="body2" fontWeight={700} sx={{ lineHeight:1.2 }}>{plan.name}</Typography>
                          {grp && (
                            <Chip label={grp.name} size="small"
                              sx={{ fontSize:9,height:17,bgcolor:alpha(grp.color,0.12),color:grp.color,border:`1px solid ${alpha(grp.color,0.3)}` }} />
                          )}
                          {plan.weekStart && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize:'0.7rem' }}>
                              {fmtDate(plan.weekStart)}{plan.weekEnd ? ' → ' + fmtDate(plan.weekEnd) : ''}
                            </Typography>
                          )}
                        </Stack>
                        {pws.length > 0 && (
                          <Stack direction="row" spacing={-0.5} sx={{ mt:0.4 }}>
                            {pws.slice(0,6).map(w => (
                              <Tooltip key={w.workerId} title={w.fullName} placement="top" disableInteractive>
                                <Avatar sx={{ width:20,height:20,fontSize:'0.55rem',
                                  bgcolor:`hsl(${(w.workerId*67)%360},50%,48%)`,
                                  border:'1.5px solid #fff' }}>
                                  {initials(w.fullName)}
                                </Avatar>
                              </Tooltip>
                            ))}
                            {pws.length > 6 && (
                              <Avatar sx={{ width:20,height:20,fontSize:'0.5rem',bgcolor:'#94A3B8',border:'1.5px solid #fff' }}>
                                +{pws.length-6}
                              </Avatar>
                            )}
                          </Stack>
                        )}
                        {!isOpen && pws.length === 0 && (plan.workerCount ?? 0) === 0 && (
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize:'0.67rem' }}>Nuk ka staf</Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={0.25} alignItems="center" flexShrink={0}>
                        <Chip label={`${plan.workerCount ?? pws.length} staf`} size="small"
                          variant="outlined" color="primary" sx={{ height:20,fontSize:10 }} />
                        <Tooltip title="Shto punëtor">
                          <IconButton size="small" color="primary" sx={{ p:0.4 }}
                            onClick={async e => { e.stopPropagation(); setAddWorkerTo(plan.id); setSelWorker(null); if(!planWorkers[plan.id]) await loadPW(plan.id); }}>
                            <PersonAddIcon sx={{ fontSize:15 }} />
                          </IconButton>
                        </Tooltip>
                        <IconButton size="small" sx={{ p:0.4 }} onClick={e => { e.stopPropagation(); setPlanDlg({...plan}); }}>
                          <EditIcon sx={{ fontSize:14 }} />
                        </IconButton>
                        <IconButton size="small" color="error" sx={{ p:0.4 }}
                          onClick={e => { e.stopPropagation(); setDelDlg({type:'plan',planId:plan.id,name:plan.name}); }}>
                          <DeleteIcon sx={{ fontSize:14 }} />
                        </IconButton>
                        <IconButton size="small" sx={{ p:0.4 }} onClick={() => toggleExpand(plan.id)}>
                          {isOpen ? <ExpandLessIcon sx={{ fontSize:16 }} /> : <ExpandMoreIcon sx={{ fontSize:16 }} />}
                        </IconButton>
                      </Stack>
                    </Box>

                    {/* Expanded worker list */}
                    {isOpen && (
                      <Box sx={{ px:2,pb:1.5,bgcolor:'#F8FFF8',borderTop:'1px dashed',borderColor:'#D1FAE5' }}>
                        {!planWorkers[plan.id] ? (
                          <Box sx={{ py:2,display:'flex',justifyContent:'center' }}><CircularProgress size={18} /></Box>
                        ) : pws.length === 0 ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display:'block',pt:1.5 }}>
                            Nuk ka staf. Shtoni punëtorë me butonin <strong>+</strong> lart.
                          </Typography>
                        ) : (
                          <Table size="small" sx={{ mt:1 }}>
                            <TableHead>
                              <TableRow>
                                {['Punëtori','Email','Shtuar me',''].map(h => (
                                  <TableCell key={h} sx={{ fontWeight:700,fontSize:10,py:0.5,color:'text.secondary',borderBottom:'1px solid #E2E8F0' }}>{h}</TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {pws.map(w => (
                                <TableRow key={w.workerId} sx={{ '&:hover':{ bgcolor:'#ECFDF5' } }}>
                                  <TableCell sx={{ py:0.6 }}>
                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                      <Avatar sx={{ width:22,height:22,fontSize:'0.58rem',bgcolor:`hsl(${(w.workerId*67)%360},50%,48%)` }}>
                                        {initials(w.fullName)}
                                      </Avatar>
                                      <Typography variant="body2" fontWeight={600} sx={{ fontSize:'0.78rem' }}>{w.fullName}</Typography>
                                    </Stack>
                                  </TableCell>
                                  <TableCell sx={{ py:0.6 }}>
                                    <Typography variant="caption" color="text.secondary">{w.email||'—'}</Typography>
                                  </TableCell>
                                  <TableCell sx={{ py:0.6 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {w.addedAt ? new Date(w.addedAt).toLocaleDateString('sq-AL',{day:'2-digit',month:'short',year:'numeric'}) : '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right" sx={{ py:0.6 }}>
                                    <IconButton size="small" color="error" sx={{ p:0.3 }}
                                      onClick={() => setDelDlg({type:'worker',planId:plan.id,workerId:w.workerId,name:w.fullName})}>
                                      <DeleteIcon sx={{ fontSize:14 }} />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ══ Dialog: Shto/Edito Grup ══════════════════════════════════════ */}
      <Dialog open={!!groupDlg} onClose={() => setGroupDlg(null)} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:2.5 } }}>
        <DialogTitle sx={{ fontWeight:800,pb:1,fontSize:'1rem' }}>
          {groupDlg?.id ? 'Edito grupin' : 'Grup / turn i ri'}
        </DialogTitle>
        <DialogContent sx={{ pt:'8px !important' }}>
          {groupDlg && (
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <TextField size="small" fullWidth label="Emri i grupit *" value={groupDlg.name}
                  onChange={e => setGroupDlg(p=>({...p,name:e.target.value}))} autoFocus />
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" fullWidth label="Përshkrim (opsional)" value={groupDlg.description||''}
                  onChange={e => setGroupDlg(p=>({...p,description:e.target.value}))} />
              </Grid>
              <Grid item xs={6}>
                <TextField size="small" fullWidth label="Fillon ora" type="time" value={groupDlg.startTime||'08:00'}
                  onChange={e => setGroupDlg(p=>({...p,startTime:e.target.value}))} InputLabelProps={{ shrink:true }} />
              </Grid>
              <Grid item xs={6}>
                <TextField size="small" fullWidth label="Mbaron ora" type="time" value={groupDlg.endTime||'16:00'}
                  onChange={e => setGroupDlg(p=>({...p,endTime:e.target.value}))} InputLabelProps={{ shrink:true }} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display:'block',mb:0.75 }}>Ditët e punës</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {DAYS.map(day => {
                    const active = (groupDlg.daysOfWeek||[]).includes(day);
                    const isWE = ['Saturday','Sunday'].includes(day);
                    return (
                      <Chip key={day} label={DAY_ALB[day]} size="small" clickable
                        onClick={() => setGroupDlg(p=>({...p,daysOfWeek:(p.daysOfWeek||[]).includes(day)?(p.daysOfWeek||[]).filter(d=>d!==day):[...(p.daysOfWeek||[]),day]}))}
                        color={active?(isWE?'error':'primary'):'default'}
                        variant={active?'filled':'outlined'}
                        sx={{ fontSize:10 }} />
                    );
                  })}
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display:'block',mb:0.75 }}>Ngjyra</Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap">
                  {PALETTE.map(c => (
                    <Box key={c} onClick={() => setGroupDlg(p=>({...p,color:c}))}
                      sx={{ width:26,height:26,borderRadius:'50%',bgcolor:c,cursor:'pointer',
                        border: groupDlg.color===c ? '3px solid #1E293B' : '2px solid rgba(0,0,0,0.1)',
                        transition:'transform .1s','&:hover':{ transform:'scale(1.2)' } }} />
                  ))}
                </Stack>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px:3,pb:2 }}>
          <Button onClick={() => setGroupDlg(null)} sx={{ textTransform:'none' }}>Anulo</Button>
          <Button variant="contained" onClick={saveGroup} disabled={loading} sx={{ textTransform:'none',borderRadius:1.5 }}>
            {loading ? <CircularProgress size={16} /> : 'Ruaj grupin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Dialog: Shto/Edito Plan ══════════════════════════════════════ */}
      <Dialog open={!!planDlg} onClose={() => setPlanDlg(null)} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:2.5 } }}>
        <DialogTitle sx={{ fontWeight:800,pb:1,fontSize:'1rem' }}>
          {planDlg?.id ? 'Edito planin' : 'Plan i ri i orarit'}
        </DialogTitle>
        <DialogContent sx={{ pt:'8px !important' }}>
          {planDlg && (
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <TextField size="small" fullWidth label="Emri i planit *" value={planDlg.name||''}
                  onChange={e => setPlanDlg(p=>({...p,name:e.target.value}))} autoFocus />
              </Grid>
              <Grid item xs={12}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Grupi / Turni (opsional)</InputLabel>
                  <Select value={planDlg.groupId||''} label="Grupi / Turni (opsional)"
                    onChange={e => setPlanDlg(p=>({...p,groupId:e.target.value}))}>
                    <MenuItem value=""><em>Pa grup</em></MenuItem>
                    {groups.map(g => (
                      <MenuItem key={g.id} value={g.id}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ width:10,height:10,borderRadius:'50%',bgcolor:g.color,flexShrink:0 }} />
                          <span>{g.name}</span>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField size="small" fullWidth label="Data fillimit" type="date" value={planDlg.weekStart||''}
                  onChange={e => setPlanDlg(p=>({...p,weekStart:e.target.value}))} InputLabelProps={{ shrink:true }} />
              </Grid>
              <Grid item xs={6}>
                <TextField size="small" fullWidth label="Data mbarimit" type="date" value={planDlg.weekEnd||''}
                  onChange={e => setPlanDlg(p=>({...p,weekEnd:e.target.value}))} InputLabelProps={{ shrink:true }} />
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" fullWidth label="Shënime" multiline rows={2} value={planDlg.notes||''}
                  onChange={e => setPlanDlg(p=>({...p,notes:e.target.value}))} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px:3,pb:2 }}>
          <Button onClick={() => setPlanDlg(null)} sx={{ textTransform:'none' }}>Anulo</Button>
          <Button variant="contained" color="secondary" onClick={savePlan} disabled={loading} sx={{ textTransform:'none',borderRadius:1.5 }}>
            {loading ? <CircularProgress size={16} /> : 'Ruaj planin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Dialog: Shto punëtor në plan ═════════════════════════════════ */}
      <Dialog open={!!addWorkerTo} onClose={() => setAddWorkerTo(null)} maxWidth="xs" fullWidth PaperProps={{ sx:{ borderRadius:2.5 } }}>
        <DialogTitle sx={{ fontWeight:800,pb:1,fontSize:'1rem' }}>Shto punëtor në plan</DialogTitle>
        <DialogContent sx={{ pt:'8px !important' }}>
          <Autocomplete
            options={workers.filter(w => {
              const existing = addWorkerTo ? (planWorkers[addWorkerTo]||[]) : [];
              return !existing.find(e => e.workerId === (w.Id||w.id));
            })}
            getOptionLabel={w => w.FullName||((w.FirstName||'')+' '+(w.LastName||'')).trim()}
            value={selWorker} onChange={(_,v) => setSelWorker(v)}
            renderInput={params => <TextField {...params} label="Zgjidh punëtorin *" size="small" autoFocus />}
            renderOption={(props,w) => (
              <li {...props} key={w.Id||w.id}>
                <Stack>
                  <Typography variant="body2" fontWeight={600}>{w.FullName}</Typography>
                  {w.Email && <Typography variant="caption" color="text.secondary">{w.Email}</Typography>}
                </Stack>
              </li>
            )}
          />
          {addWorkerTo && (planWorkers[addWorkerTo]||[]).length > 0 && (
            <Box sx={{ mt:1.5 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display:'block',mb:0.5 }}>
                Stafi aktual ({(planWorkers[addWorkerTo]||[]).length}):
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {(planWorkers[addWorkerTo]||[]).map(w => (
                  <Chip key={w.workerId} label={w.fullName} size="small" sx={{ fontSize:10,height:20 }} />
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px:2.5,pb:2 }}>
          <Button onClick={() => setAddWorkerTo(null)} sx={{ textTransform:'none' }}>Mbyll</Button>
          <Button variant="contained" startIcon={loading?<CircularProgress size={14} />:<PersonAddIcon sx={{ fontSize:15 }} />}
            onClick={addWorker} disabled={!selWorker||loading} sx={{ textTransform:'none',borderRadius:1.5 }}>
            Shto
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Dialog: Stafi i grupit ════════════════════════════════════════ */}
      <Dialog open={!!groupStaff} onClose={() => setGroupStaff(null)} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:2.5 } }}>
        <DialogTitle sx={{ fontWeight:800,pb:1,fontSize:'1rem' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {groupStaff?.color && <Box sx={{ width:12,height:12,borderRadius:'50%',bgcolor:groupStaff.color }} />}
            <span>Stafi: {groupStaff?.name}</span>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt:'4px !important' }}>
          {groupStaff && (
            <>
              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb:2 }}>
                <Autocomplete sx={{ flex:1 }}
                  options={workers.filter(w => {
                    const ex = groupWorkers[groupStaff.id]||[];
                    return !ex.find(e => e.workerId===(w.Id||w.id));
                  })}
                  getOptionLabel={w => w.FullName||((w.FirstName||'')+' '+(w.LastName||'')).trim()}
                  value={selGWorker} onChange={(_,v) => setSelGWorker(v)}
                  renderInput={params => <TextField {...params} label="Shto punëtor" size="small" />}
                  renderOption={(props,w) => (
                    <li {...props} key={w.Id||w.id}>
                      <Stack>
                        <Typography variant="body2" fontWeight={600}>{w.FullName}</Typography>
                        {w.Email&&<Typography variant="caption" color="text.secondary">{w.Email}</Typography>}
                      </Stack>
                    </li>
                  )}
                />
                <Button variant="contained" startIcon={loading?<CircularProgress size={14} />:<PersonAddIcon sx={{ fontSize:15 }} />}
                  onClick={addGWorker} disabled={!selGWorker||loading} sx={{ textTransform:'none',borderRadius:1.5,mt:0.1,height:38 }}>
                  Shto
                </Button>
              </Stack>

              {!groupWorkers[groupStaff.id] ? (
                <Box sx={{ display:'flex',justifyContent:'center',py:2 }}><CircularProgress size={20} /></Box>
              ) : groupWorkers[groupStaff.id].length === 0 ? (
                <Typography variant="body2" color="text.secondary">Ky grup nuk ka staf akoma.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor:'grey.50' }}>
                      {['Punëtori','Email','Shtuar me',''].map(h => (
                        <TableCell key={h} sx={{ fontWeight:700,fontSize:11,py:0.6 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupWorkers[groupStaff.id].map(w => (
                      <TableRow hover key={w.workerId}>
                        <TableCell sx={{ py:0.6 }}>
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Avatar sx={{ width:22,height:22,fontSize:'0.58rem',bgcolor:`hsl(${(w.workerId*67)%360},50%,48%)` }}>
                              {initials(w.fullName)}
                            </Avatar>
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize:'0.78rem' }}>{w.fullName}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ py:0.6 }}><Typography variant="caption" color="text.secondary">{w.email||'—'}</Typography></TableCell>
                        <TableCell sx={{ py:0.6 }}>
                          <Typography variant="caption" color="text.secondary">
                            {w.addedAt?new Date(w.addedAt).toLocaleDateString('sq-AL',{day:'2-digit',month:'short',year:'numeric'}):'—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py:0.6 }}>
                          <IconButton size="small" color="error" sx={{ p:0.3 }}
                            onClick={() => setDelDlg({type:'group-worker',groupId:groupStaff.id,workerId:w.workerId,name:w.fullName})}>
                            <DeleteIcon sx={{ fontSize:14 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px:2.5,pb:2 }}>
          <Button onClick={() => setGroupStaff(null)} sx={{ textTransform:'none' }}>Mbyll</Button>
        </DialogActions>
      </Dialog>

      {/* ══ Dialog: Konfirmo fshirje ══════════════════════════════════════ */}
      <Dialog open={!!delDlg} onClose={() => setDelDlg(null)} maxWidth="xs" fullWidth PaperProps={{ sx:{ borderRadius:2.5 } }}>
        <DialogTitle sx={{ fontWeight:800,fontSize:'1rem' }}>Konfirmo veprimin</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {(delDlg?.type==='worker'||delDlg?.type==='group-worker')
              ? `Hiq "${delDlg?.name||''}" nga ky ${delDlg?.type==='worker'?'plan':'grup'}?`
              : `Fshi "${delDlg?.name||''}"? Ky veprim nuk kthehet mbrapsht.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px:2.5,pb:2 }}>
          <Button onClick={() => setDelDlg(null)} sx={{ textTransform:'none' }}>Anulo</Button>
          <Button color="error" variant="contained" sx={{ textTransform:'none',borderRadius:1.5 }}
            onClick={() => {
              if(delDlg?.type==='group')        deleteGroup();
              if(delDlg?.type==='plan')         deletePlan();
              if(delDlg?.type==='worker')       removeWorker();
              if(delDlg?.type==='group-worker') removeGWorker();
            }}>
            {(delDlg?.type==='worker'||delDlg?.type==='group-worker') ? 'Hiq' : 'Fshi'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3500} onClose={() => setSnack('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} message={snack} />
    </Box>
  );
}

